export type CallerIntent =
  | 'REPRESENTATIVE'
  | 'EMERGENCY'
  | 'BOOKING'
  | 'RESCHEDULE'
  | 'CANCEL'
  | 'CONFIRM'
  | 'GENERAL_INQUIRY'
  | 'UNKNOWN';

export type RoutingEventType = 'route' | 'endCall' | 'fatal' | 'textHandoff';

export interface StreamAudioMessage {
  type: 'streamAudio';
  data: {
    audioData: string;
    audioDataType: 'raw';
    sampleRate: number;
  };
}

export interface RouteActionMessage {
  type: RoutingEventType;
  data: {
    reason: string;
    caller_intent: CallerIntent;
  };
}

export interface TranscriptMessage {
  transcript: {
    role: 'user' | 'assistant';
    text: string;
  };
}

export type ChannelType = 'VOICE' | 'MESSAGING';

export type ScheduleActionType = 'SCHEDULE' | 'RESCHEDULE' | 'CANCEL' | 'CONFIRM' | 'REQUEST';

export type ScheduleActionStatus = 'SUCCEEDED' | 'FAILED' | 'UNKNOWN';

export type PatientType = 'EXISTING' | 'NEW' | 'UNKNOWN';

export interface PhoneNumber {
  country_code: number;
  national_number: number;
}

export interface VoiceAIScheduleActionEvent {
  voice_ai_event_type: 'SCHEDULE_ACTION';
  event_timestamp: string;
  user_phone_number: PhoneNumber;
  office_phone_number: PhoneNumber | null;
  location_id: string;
  call_id: string;
  schedule_action: {
    action_type: ScheduleActionType;
    action_status: ScheduleActionStatus;
    patient_type: PatientType;
    failure_reason: string | null;
  };
}

export interface AuditAIReceptionistEvent {
  channel_type: ChannelType;
  voice_event: VoiceAIScheduleActionEvent | null;
}

export interface AIReceptionistAuditEventMessage {
  type: 'ai_receptionist_audit_event';
  event: AuditAIReceptionistEvent;
}

const ROUTING_EVENT_TYPES: ReadonlySet<RoutingEventType> = new Set(['route', 'endCall', 'fatal', 'textHandoff']);
const SCHEDULE_ACTION_TYPES: ReadonlySet<ScheduleActionType> = new Set([
  'SCHEDULE',
  'RESCHEDULE',
  'CANCEL',
  'CONFIRM',
  'REQUEST',
]);
const SCHEDULE_ACTION_STATUSES: ReadonlySet<ScheduleActionStatus> = new Set(['SUCCEEDED', 'FAILED', 'UNKNOWN']);
const CHANNEL_TYPES: ReadonlySet<ChannelType> = new Set(['VOICE', 'MESSAGING']);

export type VoiceAIRWebsocketMessage =
  | StreamAudioMessage
  | RouteActionMessage
  | TranscriptMessage
  | AIReceptionistAuditEventMessage;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

export const isStreamAudioMessage = (value: VoiceAIRWebsocketMessage): value is StreamAudioMessage => {
  if (!isRecord(value) || value.type !== 'streamAudio' || !isRecord(value.data)) {
    return false;
  }

  const audioDataType = value.data.audioDataType;
  return (
    typeof value.data.audioData === 'string' &&
    typeof value.data.sampleRate === 'number' &&
    (audioDataType === undefined || audioDataType === 'raw')
  );
};

export const isRouteActionMessage = (value: VoiceAIRWebsocketMessage): value is RouteActionMessage => {
  if (!isRecord(value) || typeof value.type !== 'string' || !ROUTING_EVENT_TYPES.has(value.type as RoutingEventType)) {
    return false;
  }

  if (!isRecord(value.data)) {
    return false;
  }

  return typeof value.data.reason === 'string' && typeof value.data.caller_intent === 'string';
};

export const isTranscriptMessage = (value: VoiceAIRWebsocketMessage): value is TranscriptMessage => {
  if (!isRecord(value) || !isRecord(value.transcript)) {
    return false;
  }

  return (
    (value.transcript.role === 'user' || value.transcript.role === 'assistant') &&
    typeof value.transcript.text === 'string'
  );
};

export const isAIReceptionistAuditEventMessage = (
  value: VoiceAIRWebsocketMessage
): value is AIReceptionistAuditEventMessage => {
  if (!isRecord(value) || value.type !== 'ai_receptionist_audit_event' || !isRecord(value.event)) {
    return false;
  }

  const channelType = value.event.channel_type;
  return typeof channelType === 'string' && CHANNEL_TYPES.has(channelType as ChannelType);
};

export const getSucceededScheduleActionType = (message: AIReceptionistAuditEventMessage): ScheduleActionType | null => {
  const voiceEvent = message.event.voice_event;

  if (!voiceEvent || message.event.channel_type !== 'VOICE' || voiceEvent.voice_ai_event_type !== 'SCHEDULE_ACTION') {
    return null;
  }

  const scheduleAction = voiceEvent.schedule_action;
  if (!scheduleAction) {
    return null;
  }

  if (
    typeof scheduleAction.action_type !== 'string' ||
    !SCHEDULE_ACTION_TYPES.has(scheduleAction.action_type as ScheduleActionType) ||
    typeof scheduleAction.action_status !== 'string' ||
    !SCHEDULE_ACTION_STATUSES.has(scheduleAction.action_status as ScheduleActionStatus)
  ) {
    return null;
  }

  return scheduleAction.action_status === 'SUCCEEDED' ? scheduleAction.action_type : null;
};
